import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const ParticleField: React.FC<{ count?: number }> = ({ count = 2000 }) => {
    const pointsRef = useRef<THREE.Points>(null!);
    const materialRef = useRef<THREE.PointsMaterial>(null!);

    // Jeder Partikel hat seinen eigenen Orbit & Phase
    const particleData = useMemo(() => {
        const rArray: number[] = [];
        const phiArray: number[] = [];
        const phaseArray: number[] = [];
        for (let i = 0; i < count; i++) {
            rArray.push(2 + Math.random() * 8);
            phiArray.push(  Math.random() * 2 * Math.PI);
            phaseArray.push(Math.random() * 2 * Math.PI);
        }
        return { rArray, phiArray, phaseArray };
    }, [count]);

    const positions = useMemo(() => {
        const arr = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            const r = particleData.rArray[i];
            const phi = particleData.phiArray[i];
            arr[i * 3] = r * Math.cos(phi);
            arr[i * 3 + 1] = r * Math.sin(phi);
            arr[i * 3 + 2] = 0;
        }
        return arr;
    }, [particleData]);

    const colors = useMemo(() => new Float32Array(count * 3), [count]);

    useFrame(({ clock }) => {
        const t = clock.getElapsedTime();
        const geo = pointsRef.current.geometry as THREE.BufferGeometry;
        const posAttr = geo.getAttribute('position') as THREE.BufferAttribute;
        const colAttr = geo.getAttribute('color') as THREE.BufferAttribute;

        for (let i = 0; i < count; i++) {
            const r = particleData.rArray[i];
            const phi0 = particleData.phiArray[i];
            const phase = particleData.phaseArray[i];
            const phi = phi0 + t * 0.2;
            const x = r * Math.cos(phi);
            const y = r * Math.sin(phi);

            posAttr.array[i * 3 + 0] = x;
            posAttr.array[i * 3 + 1] = y;

            // Farbe wechselt weich mit Phase + Zeit
            const intensity =  0.5 + 0.5 * Math.sin(t + phase);
            colAttr.array[i * 3 + 0] = 1.0 + 0.5 * intensity;      // R
            colAttr.array[i * 3 + 1] = 0.4 + 0.3 * Math.cos(t + phase + 1); // G
            colAttr.array[i * 3 + 2] = 0.6 + 0.4 * Math.sin(t * 0.5 + phase); // B
        }

        posAttr.needsUpdate = true;
        colAttr.needsUpdate = true;

        materialRef.current.size = 1 + 0.5 * Math.sin(t * 0.8);
    });

    return (
        <points ref={pointsRef}>
            <bufferGeometry>
                <bufferAttribute attach="attributes-position" args={[positions, 3]} />
                <bufferAttribute attach="attributes-color" args={[colors, 3]} />
            </bufferGeometry>
            <pointsMaterial
                ref={materialRef}
                vertexColors={false}
                size={1}
                sizeAttenuation={false}
                blending={THREE.AdditiveBlending}
                transparent={false}
                opacity={0.95}
            />
        </points>
    );
};

export default ParticleField;